import {
  AwsRdsEngine,
  ResourceType,
  type InventoryResource,
  type SourceAccount,
} from '@repo/api-gw-sdk';

import { useSourceAccounts } from '@/contexts/useSourceAccounts';

import { RDSPitrConfiguration } from './steps/rdsPitrConfiguration';

import type { Encryption } from '../encryption/encryptionComponent';
import type { Step } from '../wizard/Step';
import { Wizard } from '../wizard/Wizard';

export interface RestoreRDSPitrState {
  resourceId: string;
  dbName?: string;
  restoreToTime?: Date;
  sourceAccount?: SourceAccount;
  securityGroupIds?: string[];
  rdsInstanceClass?: string;
  rdsSubnetGroupName?: string;
  initialTags: Record<string, string>;
  tags: Record<string, string>;
  keepOriginalTags: boolean;
  encryption?: Encryption;
  vpc?: string;
  regionName: string;
  isAuroraRds: boolean;
  isPiTR: boolean;
}

const restoreRDSPitrFlow: Step<RestoreRDSPitrState> = {
  id: 'rds_pitr_configuration',
  Component: RDSPitrConfiguration,
};

export const RestoreRDSPitrWizard = ({
  resource,
  restoreToTime,
  onClose,
  onBack,
}: {
  resource: InventoryResource;
  restoreToTime?: Date;
  onClose: () => void;
  onBack: () => void;
}) => {
  const sourceAccounts = useSourceAccounts();
  const sourceAccount = sourceAccounts.sourceAccounts?.find(
    (account) => account.providerAccountId === resource.providerAccountId
  );

  if (!sourceAccount) {
    throw new Error('Source account not found');
  }

  return (
    <Wizard<RestoreRDSPitrState>
      onAbort={(origin) => (origin === 'back' ? onBack() : onClose())}
      onFinish={async () => true}
      flow={[restoreRDSPitrFlow]}
      initialState={{
        regionName: resource.region,
        resourceId: resource.id,
        keepOriginalTags: true,
        initialTags: resource?.tags || {},
        tags: resource?.tags || {},
        sourceAccount,
        restoreToTime,
        isPiTR: true,
        isAuroraRds: isAuroraRds(resource),
      }}
      title={`Restore RDS`}
      stepperLabels={['Configuration']}
    />
  );
};

const isAuroraRds = (resource: InventoryResource) => {
  return (
    resource.resourceType === ResourceType.AwsRds &&
    (resource.resourceProperties?.awsRds?.engine ===
      AwsRdsEngine.AWS_RDS_ENGINE_AURORA_MYSQL ||
      resource.resourceProperties?.awsRds?.engine ===
        AwsRdsEngine.AWS_RDS_ENGINE_AURORA_POSTGRESQL)
  );
};
